import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Navigation } from 'lucide-react';
import { Button } from './ui/button';
import logoUrl from '../assets/logo.png';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Book Your Stay', path: '/booking' },
    { name: 'Equipment Rentals', path: '/equipment' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Contact', path: '/contact' },
  ];

  // Transparent over the hero on the home page; solid midnight elsewhere or on scroll
  const wantsTransparent = isHome && !isScrolled && !isMobileMenuOpen;
  const headerClasses = wantsTransparent
    ? 'bg-transparent'
    : 'bg-[#050814]/95 backdrop-blur-md shadow-[0_4px_30px_rgba(0,0,0,0.5)] border-b border-[#B8860B]/15';

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerClasses}`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo only */}
          <Link to="/" className="flex items-center group" data-testid="site-logo-link">
            <img
              src={logoUrl}
              alt="Creek Bottom Ranch"
              className="h-14 md:h-16 w-auto select-none drop-shadow-[0_0_25px_rgba(212,175,55,0.25)] transition-transform duration-300 group-hover:scale-[1.04]"
              draggable="false"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const active = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium tracking-wide transition-all duration-200 ${
                    active
                      ? 'text-[#E5C158]'
                      : 'text-[#F4F1EA]/80 hover:text-[#E5C158]'
                  }`}
                >
                  {link.name}
                  {active && (
                    <span className="block h-px w-6 mt-0.5 mx-auto bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Right side */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:8327140101"
              className="flex items-center gap-2 text-[#F4F1EA]/80 hover:text-[#E5C158] transition-colors text-sm"
              data-testid="header-phone-link"
            >
              <Phone size={15} />
              <span>(832) 714-0101</span>
            </a>
            <Link to="/directions" data-testid="header-take-me-there-link">
              <Button
                data-testid="header-take-me-there-btn"
                variant="outline"
                className="bg-transparent border-[#B8860B] text-[#E5C158] hover:bg-[#B8860B]/10 hover:text-[#F4F1EA] rounded-full px-4 gap-1.5"
              >
                <Navigation size={14} />
                Take Me There
              </Button>
            </Link>
            <Link to="/booking">
              <Button
                data-testid="header-reserve-btn"
                className="bg-[#B8860B] hover:bg-[#D4AF37] text-[#050814] font-semibold rounded-full px-5 shadow-[0_0_22px_rgba(184,134,11,0.35)] hover:shadow-[0_0_28px_rgba(212,175,55,0.55)] transition-all"
              >
                Reserve Now
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-[#F4F1EA] hover:text-[#E5C158] transition-colors"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-[#B8860B]/20" data-testid="mobile-menu">
            <nav className="flex flex-col space-y-1">
              {navLinks.map((link) => {
                const active = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                      active
                        ? 'text-[#E5C158] bg-[#B8860B]/10'
                        : 'text-[#F4F1EA]/85 hover:text-[#E5C158]'
                    }`}
                  >
                    {link.name}
                  </Link>
                );
              })}
              <a
                href="tel:8327140101"
                className="flex items-center gap-2 px-4 py-3 text-[#F4F1EA]/85 hover:text-[#E5C158]"
              >
                <Phone size={16} />
                <span className="text-sm">(832) 714-0101</span>
              </a>
              <Link
                to="/directions"
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="mobile-take-me-there-link"
                className="flex items-center gap-2 px-4 py-3 text-[#E5C158] hover:text-[#F4F1EA]"
              >
                <Navigation size={16} />
                <span className="text-sm font-medium">Take Me There</span>
              </Link>
              <Link to="/booking" onClick={() => setIsMobileMenuOpen(false)} className="mx-3 mt-2">
                <Button className="w-full bg-[#B8860B] hover:bg-[#D4AF37] text-[#050814] font-semibold rounded-full">
                  Reserve Now
                </Button>
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
